import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { PatientService } from "../../../../../services/patient.service";
import { ToastrService } from "../../../../../services/toastr.service";
import { useEditReferralHH } from "../../../store/useEditReferralHH";
import { IDiagnosisResponse } from "../../../../../resources-vue/vue-interfaces/i-diagnosis";

import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";

export default defineComponent({
    components: {
        FormSection,
        TypeaheadInput
    },
    data() {
        return {
            primaryDiagnosis: { diagnosis: "", code: "" },
            otherDiagnosis: [] as IDiagnosisResponse[],
            _toastrService: null,
            _diagnosisService: null,
            questionableEncounterCode: false as boolean
        };
    },

    computed: {
        ...mapState(useEditReferralHH, {
            referral: "referral",
            application: "currentApplication",
            accountId: "accountId",
            referralOptions: "referralOptions",
            errors: "errors"
        }),
        isPrimaryDiagnosisError() {
            return this.errors.Clinical.length ? true : false;
        }
    },

    async created() {
        this._diagnosisService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
        this.setOtherDiagnosis();
    },

    mounted() {
        if (this.referral.Clinical.PrimaryPatientDiagnosisCodes.DiagnosisCode) {
            this.primaryDiagnosis = {
                diagnosis: this.referral.Clinical.PrimaryPatientDiagnosisCodes.LongDescription,
                code: this.referral.Clinical.PrimaryPatientDiagnosisCodes.DiagnosisCode
            };
            this.referral.Clinical.PrimaryPatientDiagnosisCodes.Id = "";
        }
    },

    watch: {
        primaryDiagnosis(value: any) {
            if (!value.code) {
                this.errors.Clinical.push("PrimaryPatientDiagnosisCodes");
            } else {
                this.errors.Clinical = this.errors.Clinical.filter(
                    (val: string) => val !== "PrimaryPatientDiagnosisCodes"
                );
            }
        }
    },

    methods: {
        addDiagnosis() {
            this.otherDiagnosis.push({ diagnosis: "", code: "" });
            this.referral.Clinical.PatientDiagnosisCodes.push(this.referral.Clinical.PrimaryPatientDiagnosisCodes);
        },

        removeDiagnosis(index: number) {
            this.otherDiagnosis.splice(index, 1);
            this.referral.Clinical.PatientDiagnosisCodes.splice(index, 1);
        },

        async diagnosisFetchByDiagnosis(event: any) {
            if (event?.length > 2) {
                let result;
                try {
                    const data = await this._diagnosisService.patientDiagnosis({
                        descriptionSearch: event,
                        codeSearch: "",
                        agencyId: this.accountId,
                        application: this.application,
                        page: 1,
                        limit: 10
                    });

                    return data?.Diagnoses.map((item: any) => {
                        return {
                            name: item.Description,
                            value: item.Code,
                            clinicalGrouping: item.ClinicalGrouping,
                            description: item.Description
                        };
                    });
                } catch (error) {
                    this._toastrService.error({
                        title: `Error`,
                        message: `There was a problem while getting this referral. Please try again. If the problem persists, please report it.`
                    });
                }
                return result;
            }
        },

        async setOtherDiagnosis() {
            if (
                this.referral.Clinical.PatientDiagnosisCodes.length > 0 &&
                this.referral.Clinical.PatientDiagnosisCodes[0].DiagnosisCode
            ) {
                this.otherDiagnosis = await this.referral.Clinical.PatientDiagnosisCodes.map(
                    (item: any, index: any) => {
                        return {
                            diagnosis: item.LongDescription,
                            code: item.DiagnosisCode,
                            index: index
                        };
                    }
                );
            }
        },

        async diagnosisFetchByCode(event: any) {
            if (event.length > 2) {
                let result;
                try {
                    const data = await this._diagnosisService.patientDiagnosis({
                        descriptionSearch: "",
                        codeSearch: event,
                        agencyId: this.accountId,
                        application: this.application,
                        page: 1,
                        limit: 10
                    });

                    return data?.Diagnoses?.map((item: any) => {
                        return {
                            name: item.Code,
                            value: item.Code,
                            clinicalGrouping: item.ClinicalGrouping,
                            description: item.Description
                        };
                    });
                } catch (error) {
                    this._toastrService.error({
                        title: `Error`,
                        message: `There was a problem while getting this referral. Please try again. If the problem persists, please report it.`
                    });
                }
                return result;
            }
        },

        primaryDiagnosisChanged(newValue: any) {
            const desc = newValue?.description.split(" ").slice(0, 3).join(" ");
            this.primaryDiagnosis = { diagnosis: newValue?.description, code: newValue?.value };
            this.referral.Clinical.PrimaryPatientDiagnosisCodes = {
                ...this.referral.Clinical.PrimaryPatientDiagnosisCodes,
                IsPrimary: true,
                Id: "",
                DiagnosisCode: newValue?.value,
                ShortDescription: desc,
                LongDescription: newValue?.description
            };
            if (newValue?.clinicalGrouping === 0) {
                this.questionableEncounterCode = true;
                this._toastrService.warning(
                    {
                        title: `Warning`,
                        message: `This diagnosis is a questionable encounter code and will not calculate a grouper payment under PDGM. Contact the physician to obtain a more definitive primary diagnosis that is payable under PDGM.`,
                        options: {
                            extendedTimeOut: 0,
                            timeOut: 0,
                            positionClass: "toast-top-right-hh",
                            toastClass: "toast-warning-hh",
                            closeClass: "toast-close-button-hh"
                        }
                    },
                    true
                );
            } else {
                this.questionableEncounterCode = false;
            }
        },

        otherDiagnosisChanged(newValue: any, id: any) {
            const desc = newValue?.description.split(" ").slice(0, 3).join(" ");
            this.otherDiagnosis[id] = { diagnosis: newValue?.description, code: newValue?.value };
            this.referral.Clinical.PatientDiagnosisCodes[id] = {
                ...this.referral.Clinical.PrimaryPatientDiagnosisCodes,
                IsPrimary: false,
                Id: "",
                DiagnosisCode: newValue?.value,
                LongDescription: newValue?.description,
                ShortDescription: desc
            };
        }
    }
});
